import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { user } from '../../../../store/reducers/user';
import moment from 'moment';
import { calculateArrivalDate } from '../../../../util/patientUtil';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import _, { find, isEmpty, startsWith } from 'lodash';
import keyPerformanceIndicator from '../../../../util/timeMarker';
import Notes from '../../../../util/notes';
import Formatter from '../../../../util/formatter';
import MessageBox from '../../../../util/messageBox';
import { patientBanner } from '../../../../controls/agyle/tasks/components/patientBanner';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import NewNoteModal from '../../../../components/notes/component/newNoteModal';
import useOpenState from '../../../../hooks/useOpenState';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import { getConfig } from '../../../../util/configUtil';
import { consultantDictionary, getConsultantName } from '../../../../controls/agyle/clinician/clinitianUtils';
import AcuityScoreControls from '../../../../components/forms/acuityScore/acuityScoreControls';
import { setSnackbar } from '../../../../store/reducers/globals';
import useCliniciansAndNursesDict from '../../../../hooks/useCliniciansAndNursesDict';
import useConsultantsDict from '../../../../hooks/useConsultantsDict';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import { ctxReferralFormData, openPatientForm } from '../../../../store/reducers/patientContext';
import { acuity, getEcdsVersion } from '../../../../store/reducers/dicts';
import { isMTSVersion } from '../../../../components/forms/acuityScore/acuityScoreUtils';

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.paperDarker,
    },
    dialogTitle: {
        textAlign: 'center',
        padding: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: '24px',
        paddingRight: '24px',
    },

    closeButton: {
        width: '30px',
        height: '30px',
        paddingTop: '13px',
        paddingRight: '10px',
    },
    dialogContent: {
        height: 'auto',
    },
    error: {
        color: '#f44336',
    },
    paper: {
        background: theme.palette.background.paperDarker,
        padding: 10,
        marginBottom: 10,
    },
    margin: {
        marginTop: 5,
    },
}));

const InitialAssessment = (props) => {
    const { t } = useTranslation();
    const classesLocal = useStyles();
    const dispatch = useDispatch();

    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    // Acuity find
    const referralData = useSelector(ctxReferralFormData);
    const ecdsAcuityDict = useSelector(acuity);
    const version = dispatch(getEcdsVersion(props.patient?.admission?.startDate));

    // Dict
    const [nursesCliniciansList, userFind] = useCliniciansAndNursesDict('forTriage');
    const [cliniciansList, userFind2] = useConsultantsDict(consultantDictionary.triage);

    const [initialAssessment, setInitialAssessment] = useState({
        who: null,
        when: moment().toISOString(),
        timestamp: null,
        additionalInformation: '',
        presentation: null,
        discriminator: null,
        status: 'new',
    });
    const [org, setOrg] = useState(null);
    const [isError, setIsError] = useState(false);
    const [errorPicker, setErrorPicker] = useState(null);
    const [formIsModified, setFormIsModified] = React.useState(props.isActionMode);

    const [fullFormMode, _setFullFormMode] = React.useState(!props.isActionMode);

    const [openConfirmDeleted, setOpenConfirmDeleted] = React.useState(false);
    const [isOpenNoteOpen, openOpenNote, closeOpenNote] = useOpenState(false, true);

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const clinicalNotesFromForm = getConfig('clinicalNotesFromForm', false);
    const consultantComponentEnabled = getConfig('consultantComponentEnabled', false);

    // Init data
    useEffect(() => {
        if (props.patient?.initialAssessment) {
            setInitialAssessment((prevState) => ({
                ...prevState,
                ...props.patient?.initialAssessment,
                when: props.patient?.initialAssessment?.when ? props.patient?.initialAssessment?.when : moment().toISOString(),
            }));
            setOrg({ ...props.patient?.initialAssessment });

            if (
                props.patient.initialAssessment.who &&
                props.patient.initialAssessment.when &&
                props.patient.initialAssessment.status === 'timestamp'
            )
                setFormIsModified(true);
        } else if (referralData) {
            const isMTS = isMTSVersion(null, props.patient?.admission?.startDate);
            if (!isMTS) {
                const acuity = find(
                    ecdsAcuityDict,
                    (ref) => ref.ecdsVersion === version && startsWith(ref?.__description, referralData?.ecdsAcuityScore),
                );
                if (!acuity) return;

                setInitialAssessment((prevState) => ({
                    ...prevState,
                    discriminator: acuity,
                }));
            }
        }
    }, [referralData, props.patient?.initialAssessment]);

    // Set clinician
    useEffect(() => {
        if (consultantComponentEnabled) {
            if (userFind2 && !initialAssessment?.who && !initialAssessment?.presentation) {
                setInitialAssessment((prev) => ({
                    ...prev,
                    who: userFind2,
                }));
            }
        } else {
            if (userFind && !initialAssessment?.who && !initialAssessment?.triageClinicians && !initialAssessment?.presentation) {
                setInitialAssessment((prev) => ({
                    ...prev,
                    who: userFind,
                }));
            }
        }
    }, [userFind, userFind2]);

    const checkDifferent = (path, isDate) => {
        const newVal = _.get(initialAssessment, path);
        const orgVal = _.get(org, path);

        if (orgVal && orgVal !== newVal) {
            return (
                `changed from ${isDate ? moment(orgVal).format(defaultDateTimeFormat) : orgVal} ` +
                `to ${isDate ? moment(newVal).format(defaultDateTimeFormat) : newVal}`
            );
        }
        return isDate ? moment(newVal).format(defaultDateTimeFormat) : newVal;
    };

    const prepareNote = (isClear) => {
        return !fullFormMode
            ? Notes.getTimestampInitialAssessmentFormNote(checkDifferent('who.__description', false), checkDifferent('when', true))
            : Notes.getInitialAssessmentFormNote(
                  initialAssessment,
                  isClear,
                  tester,
                  checkDifferent('who.__description', false),
                  checkDifferent('when', true),
              );
    };

    const handleClear = () => {
        const data = [
            {
                name: 'initialAssessment',
                value: {
                    who: null,
                    when: null,
                    timestamp: moment().toISOString(),
                    additionalInformation: '',
                    discriminator: null,
                    presentation: null,
                    by: tester,
                    byLogin: userObject?.userName,
                    status: 'new',
                },
                noteToAdd: prepareNote(true),
            },
        ];
        setOpenConfirmDeleted(false);
        props.onChange(data);
        props.onClose();
    };

    const checkFormValidity = () => {
        if (!fullFormMode) {
            setIsError(!initialAssessment?.who || errorPicker);
            return initialAssessment?.who && initialAssessment?.when && !errorPicker;
        } else {
            const error =
                !initialAssessment?.who ||
                errorPicker ||
                isEmpty(initialAssessment?.additionalInformation) ||
                !initialAssessment.discriminator;
            setIsError(error);
            if (error) {
                dispatch(setSnackbar({ type: 'warning', message: t('Please fill in all required fields before submitting.') }));
            }
            return (
                initialAssessment?.who &&
                initialAssessment?.when &&
                !errorPicker &&
                !isEmpty(initialAssessment?.additionalInformation) &&
                initialAssessment.discriminator
            );
        }
    };

    const handleSubmitChange = () => {
        if (checkFormValidity()) {
            const objToSave = [
                {
                    name: 'initialAssessment',
                    value: {
                        ...initialAssessment,
                        timestamp: moment().toISOString(),
                        by: tester,
                        byLogin: userObject?.userName,
                        status: fullFormMode ? 'completed' : 'timestamp',
                    },
                    noteToAdd: prepareNote(false),
                },
            ];

            keyPerformanceIndicator(objToSave, props.patient, ['firstInitialAssessment'], {
                when: initialAssessment.when,
                who: initialAssessment.who,
            });

            props.onChange(objToSave);
            props.onClose();
        }
    };

    const handleOpenFullFormTimestamp = () => {
        if (props.handleOpenFullFormTimestamp) props.handleOpenFullFormTimestamp();
    };

    const openTreatmentPlan = () => {
        dispatch(openPatientForm('treatmentPlan'));
    };

    const getAdditionalButtons = () => {
        const buttons = [];

        if (!fullFormMode) {
            buttons.push(
                <AgyleButton variant="contained" color="primary" onClick={handleOpenFullFormTimestamp}>
                    {t('Open form')}
                </AgyleButton>,
            );
        }

        if (!props.disableSave) {
            buttons.push(
                <AgyleButton variant="contained" disabled={props.workflowDisabled} onClick={openTreatmentPlan}>
                    {t('Treatment Plan Form')}
                </AgyleButton>,
            );
            if (clinicalNotesFromForm) {
                buttons.push(
                    <AgyleButton variant="outlined" onClick={openOpenNote} key="add-note">
                        {t('Add note')}
                    </AgyleButton>,
                );
            }
            buttons.push(
                <AgyleButton variant="outlined" onClick={() => setOpenConfirmDeleted(true)} key="delete">
                    {t('Delete')}
                </AgyleButton>,
            );
        }
        return buttons;
    };

    return (
        <>
            <MiniFormDialog
                title={t(props._formTitle)}
                open={props.open}
                onClose={props.onClose}
                disableSubmit={!formIsModified}
                onSave={handleSubmitChange}
                buttons={getAdditionalButtons()}
            >
                <Grid container spacing={2}>
                    {!fullFormMode && (
                        <Grid item xs={12}>
                            {patientBanner(props.patient, true)}
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <AgyleDictionaryField
                            value={initialAssessment.who}
                            label={t('Initial Assessment Clinician')}
                            disabled={props.disableSave}
                            withBackground
                            getOptionLabel={(option) => getConsultantName(option)}
                            options={consultantComponentEnabled ? cliniciansList : nursesCliniciansList}
                            groupBy={(opt) => opt.group}
                            isOptionEqualToValue={(option, value) => option.__id === value.__id}
                            onChange={(_, value) => {
                                setInitialAssessment((prevState) => ({
                                    ...prevState,
                                    who: value,
                                }));
                                setFormIsModified(true);
                            }}
                            size="large"
                            noBorder={false}
                            withNewBackground
                            error={isError && !initialAssessment.who}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDatePicker
                            type="dateTime"
                            label={t('Date and Time')}
                            error={isError && !initialAssessment?.when}
                            value={initialAssessment.when}
                            minDate={moment(calculateArrivalDate(props.patient))}
                            maxDate={moment()}
                            onError={setErrorPicker}
                            onChange={(value) => {
                                setInitialAssessment((prevState) => ({
                                    ...prevState,
                                    when: moment(value).toISOString(),
                                }));
                                setFormIsModified(true);
                            }}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>
                </Grid>

                {fullFormMode && (
                    <Grid container spacing={2} className={classesLocal.margin}>
                        <Grid item xs={12}>
                            <AgyleTextField
                                disabled={props.disableSave}
                                label={t('Initial Assessment Summary')}
                                multiline
                                value={initialAssessment.additionalInformation}
                                error={isError && isEmpty(initialAssessment?.additionalInformation)}
                                onChange={(event) => {
                                    setInitialAssessment((prevState) => ({
                                        ...prevState,
                                        additionalInformation: event.target.value,
                                    }));
                                    setFormIsModified(true);
                                }}
                                fullWidth
                                debounce
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <AcuityScoreControls
                                value={initialAssessment}
                                onChange={(value) => {
                                    setInitialAssessment(value);
                                    setFormIsModified(true);
                                }}
                                startDate={props.patient?.admission?.startDate}
                                canEdit={!props?.disableSave}
                                isSmall={false}
                                isError={isError && !initialAssessment?.discriminator}
                                noMargin
                                isTriage={false}
                            />
                        </Grid>
                    </Grid>
                )}
            </MiniFormDialog>

            <MessageBox
                title={t('CAUTION')}
                haveAccept={{ yes: t('Yes'), no: t('No') }}
                onAccept={handleClear}
                onClose={() => setOpenConfirmDeleted(false)}
                message={t('Are you sure you want to delete all the recorded values?')}
                open={openConfirmDeleted}
            />

            <NewNoteModal
                open={isOpenNoteOpen}
                onClose={closeOpenNote}
                patient={props.patient}
                disableSave={props.disableSave}
                fromForm="Rapid Assessment"
            />
        </>
    );
};

export default InitialAssessment;
